import React, { useState, useEffect } from 'react';
import { 
  Bell, CheckCircle2, AlertTriangle, Info, FileText, Briefcase,
  CheckSquare, X, Loader2, Check, ChevronRight, Clock, Filter,
  Trash2, RefreshCw
} from 'lucide-react';
import { useUserEmail } from '../../context/UserAuthContext';
import { useNavigate } from 'react-router-dom';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const NOTIFICATION_TYPES = {
  info: { icon: Info, color: 'bg-blue-500/20 text-blue-400', label: 'Info' },
  success: { icon: CheckCircle2, color: 'bg-green-500/20 text-green-400', label: 'Success' },
  warning: { icon: AlertTriangle, color: 'bg-yellow-500/20 text-yellow-400', label: 'Warning' },
  urgent: { icon: AlertTriangle, color: 'bg-red-500/20 text-red-400', label: 'Urgent' },
  task: { icon: CheckSquare, color: 'bg-purple-500/20 text-purple-400', label: 'Task' },
  content: { icon: FileText, color: 'bg-cyan-500/20 text-cyan-400', label: 'Content' },
  partnership: { icon: Briefcase, color: 'bg-orange-500/20 text-orange-400', label: 'Partnership' }
};

const AENotifications = () => {
  const userEmail = useUserEmail();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState('all'); // all, unread
  const [markingAll, setMarkingAll] = useState(false);

  useEffect(() => {
    if (userEmail) {
      fetchNotifications();
    }
  }, [userEmail, filter]);

  const fetchNotifications = async () => {
    try {
      let url = `${API}/api/always-elevate/notifications?user_email=${encodeURIComponent(userEmail)}`;
      if (filter === 'unread') {
        url += '&unread_only=true';
      }
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) {
        setNotifications(data.data);
        setUnreadCount(data.unread_count);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await fetch(`${API}/api/always-elevate/notifications/${notificationId}/read?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST'
      });
      fetchNotifications();
    } catch (err) {
      console.error('Error marking notification read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await fetch(`${API}/api/always-elevate/notifications/mark-all-read?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST'
      });
      fetchNotifications();
    } catch (err) {
      console.error('Error marking all read:', err);
    } finally {
      setMarkingAll(false);
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.read_by?.includes(userEmail)) {
      handleMarkRead(notification.id);
    }
    // Navigate if link exists
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const isUnread = (notification) => {
    return !notification.read_by?.includes(userEmail);
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C4A052]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ae-notifications-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#C4A052] text-sm font-medium tracking-wider">ALWAYS ELEVATE</p>
              <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: 'Anton, sans-serif' }} data-testid="ae-notifications-title">
                Notifications
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchNotifications}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                data-testid="refresh-notifications-btn"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  disabled={markingAll}
                  className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors disabled:opacity-50"
                  data-testid="mark-all-read-btn"
                >
                  {markingAll ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Mark All Read
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="flex items-center gap-2 mb-6">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
              filter === 'all' ? 'bg-[#C4A052] text-black font-medium' : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
            data-testid="filter-all"
          >
            All
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-4 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
              filter === 'unread' ? 'bg-[#C4A052] text-black font-medium' : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
            data-testid="filter-unread"
          >
            Unread
            {unreadCount > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                filter === 'unread' ? 'bg-black/20' : 'bg-[#C4A052] text-black'
              }`}>
                {unreadCount}
              </span>
            )}
          </button>
        </div>

        {/* Notifications List */}
        {notifications.length === 0 ? (
          <div className="bg-black border border-white/20 rounded-xl p-12 text-center">
            <Bell className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">No Notifications</h3>
            <p className="text-gray-500 text-sm">
              {filter === 'unread' ? 'All caught up! No unread notifications.' : 'No notifications yet.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="notifications-list">
            {notifications.map(notification => {
              const notifType = NOTIFICATION_TYPES[notification.notification_type] || NOTIFICATION_TYPES.info;
              const NotifIcon = notifType.icon;
              const unread = isUnread(notification);
              
              return (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`bg-white/5 border rounded-xl p-4 cursor-pointer ${
                    unread ? 'border-[#C4A052]/30 bg-[#C4A052]/5' : 'border-white/10'
                  }`}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`p-2.5 rounded-xl ${notifType.color} flex-shrink-0`}>
                      <NotifIcon className="w-5 h-5" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h4 className={`font-medium ${unread ? 'text-white' : 'text-gray-300'}`}>
                            {notification.title}
                          </h4>
                          <p className="text-sm text-gray-400 mt-1">{notification.message}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-xs text-gray-500">{getTimeAgo(notification.created_at)}</span>
                          {unread && (
                            <div className="w-2 h-2 rounded-full bg-[#C4A052]" />
                          )}
                        </div>
                      </div>
                      
                      {/* Action Link */}
                      {notification.link && (
                        <div className="flex items-center gap-1 mt-2 text-[#C4A052] text-sm">
                          <span>View details</span>
                          <ChevronRight className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Stats Summary */}
        {notifications.length > 0 && (
          <div className="mt-8 grid grid-cols-4 gap-4">
            {Object.entries(
              notifications.reduce((acc, n) => {
                acc[n.notification_type] = (acc[n.notification_type] || 0) + 1;
                return acc;
              }, {})
            ).slice(0, 4).map(([type, count]) => {
              const notifType = NOTIFICATION_TYPES[type] || NOTIFICATION_TYPES.info;
              const NotifIcon = notifType.icon;
              return (
                <div key={type} className="bg-black border border-white/20 rounded-xl p-4 text-center">
                  <div className={`w-10 h-10 mx-auto rounded-lg ${notifType.color} flex items-center justify-center mb-2`}>
                    <NotifIcon className="w-5 h-5" />
                  </div>
                  <p className="text-xl font-bold">{count}</p>
                  <p className="text-xs text-gray-400 capitalize">{type}</p>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default AENotifications;
